/**
 * Workload Balancer page — shows per-role overload + auto-suggest reassignment.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Users, Loader2, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioWorkload() {
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [s, w] = await Promise.all([
        axios.get(`${API}/api/studio/workload/suggestions`, { withCredentials: true }),
        axios.get(`${API}/api/studio/workload`, { withCredentials: true }),
      ]);
      setData(s.data.suggestions || []);
      const b = w.data.buckets;
      setCounts(Array.isArray(b) ? b : []);
    } catch (_) { setData([]); setCounts([]); }
  };
  useEffect(() => { load(); }, []);

  const reassign = async (s, candidate) => {
    setBusy(candidate._id);
    try {
      await axios.post(`${API}/api/studio/workload/reassign`, {
        content_id: candidate._id,
        role: s.role,
        new_user_id: s.to_user,
      }, { withCredentials: true });
      toast.success("Reassigned");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <Link to="/studio" className="studio-back" data-testid="wl-back">
          <ArrowLeft size={14}/> Back to Studio
        </Link>
        <div className="studio-section-h" style={{ marginTop: 12 }}>
          <div>
            <span className="studio-eyebrow"><Users size={12}/> Workload balancer</span>
            <h2 style={{ margin: "6px 0 0" }}>Who&apos;s overloaded &amp; suggested rebalance</h2>
          </div>
          <button className="studio-btn-ghost" onClick={load} data-testid="wl-refresh">
            <RefreshCw size={14}/> Refresh
          </button>
        </div>

        {/* Current workload counts */}
        <div className="studio-card" style={{ marginTop: 14 }}>
          <h3>Open tasks per teammate</h3>
          {counts === null ? (
            <Loader2 size={18} className="ac-spin"/>
          ) : counts.length === 0 ? (
            <p className="studio-muted">No assignees yet.</p>
          ) : (
            <table className="studio-table" data-testid="wl-counts-table">
              <thead><tr><th>Member</th><th>Total open</th><th>By role</th></tr></thead>
              <tbody>
                {counts.map((b) => (
                  <tr key={b.user_id}>
                    <td>{b.user?.name || b.user?.email || b.user_id.slice(0, 10)}</td>
                    <td><strong>{b.counts.total || 0}</strong></td>
                    <td style={{ fontSize: "0.85rem", color: "var(--st-text-soft)" }}>
                      {Object.entries(b.counts).filter(([k]) => k !== "total").map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Suggestions */}
        <div style={{ marginTop: 18 }}>
          {data === null ? (
            <div className="studio-card studio-loading"><Loader2 size={22} className="ac-spin"/></div>
          ) : data.length === 0 ? (
            <div className="studio-card"><p className="studio-muted">No imbalance detected — everyone&apos;s evenly loaded ✨</p></div>
          ) : (
            data.map((s, i) => (
              <div key={i} className="studio-card" style={{ marginBottom: 14 }} data-testid={`wl-suggestion-${i}`}>
                <h3 style={{ textTransform: "capitalize" }}>{s.role} rebalance</h3>
                <p>
                  <strong style={{ color: "#EF4444" }}>{s.from_user.slice(0, 10)}…</strong> has <strong>{s.from_count}</strong> open tasks ·
                  <strong style={{ color: "#10B981", marginLeft: 6 }}>{s.to_user.slice(0, 10)}…</strong> has only <strong>{s.to_count}</strong>.
                  Move <strong>{s.move_count}</strong> task(s) below:
                </p>
                <ul className="studio-ops-list" style={{ marginTop: 10 }}>
                  {s.candidates.map((c) => (
                    <li key={c._id} className="studio-ops-li" style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                      <div><strong>{c.title}</strong></div>
                      <button
                        className="studio-btn-primary"
                        onClick={() => reassign(s, c)}
                        disabled={busy === c._id}
                        data-testid={`wl-reassign-${c._id}`}
                      >
                        {busy === c._id ? <Loader2 size={12} className="ac-spin"/> : <ArrowRight size={12}/>} Move
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
